import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useId, useState } from "react";

import { customerDetail, customerStageText, missingCustomerFields, type CustomerRequest } from "@ravnar/api-contract";

import { CustomerForm } from "../-components/customer-form";
import { MissingDetails } from "../-components/missing-details";
import { NoAccess } from "../-components/no-access";
import { PartyDocuments } from "../-components/party-documents";
import { ApiError } from "../../../api/client";
import { customerQueryOptions, removeCustomer, updateCustomer } from "../../../api/customers";
import { useAskAgain } from "../../../api/refresh";
import { sessionQueryOptions, useSession } from "../../../api/session";
import { PageHeader } from "../../../components/shell/app-shell";
import { Button } from "../../../components/ui/button";
import { buttonClassName } from "../../../components/ui/button-styles";
import { Card, CardHeader } from "../../../components/ui/card";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog";
import { showToast } from "../../../lib/toast";
import { DIRECTORY_DESCRIPTION } from "../index";

export const Route = createFileRoute("/directory/customers/$customerId")({
	loader: async ({ context, params }) => {
		// A member with no access to the Directory is told so by the page; asking the API would only answer 403.
		const session = await context.queryClient.query(sessionQueryOptions());
		if (session === null || session.member.access.directory === "none") {
			return;
		}
		try {
			return await context.queryClient.query({
				...customerQueryOptions(params.customerId),
				staleTime: "static",
			});
		} catch (error) {
			// Somebody removed it, or the id was typed by hand: the router's not-found page says so.
			if (error instanceof ApiError && error.status === 404) {
				notFound({ throw: true });
			}
			throw error;
		}
	},
	head: ({ loaderData }) => ({ meta: [{ title: `${loaderData?.name ?? "Customer"} · Directory · Ravnar` }] }),
	staticData: { crumbs: ["Directory", "Customer"] },
	component: CustomerPage,
});

interface CustomerDetailProps {
	/** Only an editor of the Directory may change the record, save the note, or remove the customer. */
	readonly canEdit: boolean;
}

function CustomerDetail({ canEdit }: CustomerDetailProps) {
	const { customerId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const { data: customer } = useSuspenseQuery(customerQueryOptions(customerId));
	const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false);
	const formId = useId();

	// A change to a customer touches the kind's own queries and the audit log, which lives under "settings"; the
	// Directory that projects it and the entry the queue carries both land after the answer, so they are asked again.
	const refreshCustomer = () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: ["customers"] }),
			queryClient.invalidateQueries({ queryKey: ["settings"] }),
			// Saving or removing a party closes the work item that asked for it, which the Work page and the rail count.
			queryClient.invalidateQueries({ queryKey: ["work"] }),
			askAgain(["directory"], ["settings", "audit-log"]),
		]);
	const saveMutation = useMutation({
		mutationFn: (input: CustomerRequest) => updateCustomer(customerId, input),
		onSuccess: refreshCustomer,
	});
	const removeMutation = useMutation({ mutationFn: () => removeCustomer(customerId), onSuccess: refreshCustomer });

	const saveCustomer = async (input: CustomerRequest) => {
		try {
			await saveMutation.mutateAsync(input);
		} catch {
			// The alert under the form shows saveMutation.error.
			return;
		}
		showToast(`Changes to ${input.name} saved.`);
	};

	const removeCustomerForGood = async () => {
		try {
			await removeMutation.mutateAsync();
		} catch {
			// The dialog's own error prop shows removeMutation.error, which is why it does not close itself.
			return;
		}
		setIsConfirmingRemoval(false);
		void navigate({ to: "/directory", search: {} });
		showToast(`${customer.name} removed from the Directory.`);
	};

	return (
		<div className="max-w-[880px]">
			<PageHeader
				title={customer.name}
				description={`${customerStageText(customer)} · ${customerDetail(customer)}`}
				actions={
					<>
						<Link to="/directory" search={{}} className={buttonClassName({ size: "md" })}>
							Back to Directory
						</Link>
						{canEdit ? (
							<Button
								className="text-alert hover:text-alert"
								onClick={() => {
									setIsConfirmingRemoval(true);
								}}
							>
								Remove customer
							</Button>
						) : null}
					</>
				}
			/>
			<MissingDetails missing={missingCustomerFields(customer)} noun="customer" />
			<Card>
				<CardHeader title="Customer" />
				<div className="p-4">
					<CustomerForm
						key={customer.id}
						formId={formId}
						customer={customer}
						canEdit={canEdit}
						onSave={saveCustomer}
					/>
				</div>
			</Card>
			{saveMutation.error === null ? null : (
				<p role="alert" className="mt-3.5 text-caption text-alert">
					{saveMutation.error.message}
				</p>
			)}
			{canEdit ? (
				<div className="mt-3.5 flex items-center justify-end gap-2">
					<Button type="submit" form={formId} variant="primary" disabled={saveMutation.isPending}>
						{saveMutation.isPending ? "Saving…" : "Save changes"}
					</Button>
				</div>
			) : null}
			<PartyDocuments kind="customer" partyId={customer.id} name={customer.name} />
			<ConfirmDialog
				open={isConfirmingRemoval}
				title={`Remove ${customer.name}?`}
				confirmLabel="Remove customer"
				busy={removeMutation.isPending}
				error={removeMutation.error?.message}
				onConfirm={() => {
					void removeCustomerForGood();
				}}
				onClose={() => {
					removeMutation.reset();
					setIsConfirmingRemoval(false);
				}}
			>
				Its record and its note are deleted for good. The audit log keeps that it happened.
			</ConfirmDialog>
		</div>
	);
}

function CustomerPage() {
	const { member } = useSession();
	return member.access.directory === "none" ? (
		<NoAccess description={DIRECTORY_DESCRIPTION} />
	) : (
		<CustomerDetail canEdit={member.access.directory === "editor"} />
	);
}
